import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Typography, Avatar } from '@mui/material';
import { compact } from 'lodash';

import { OrderActiveFilters } from '~components/index';
import { OrderActiveFiltersItem } from '~components/OrderActiveFilters/types';
import { Nullable } from '~globals/types/commons';
import { formatterDate } from '~utils/formatter';
import { getDefaultAvatar } from '~utils/user';

// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierCollectFiltersContext } from '../../../../../CollectFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../Context';

import { DashboardVariantV2CarrierContentActionsContainer } from '../../../../styles';

const DashboardVariantV2CarrierContentCollectTabCurrenFilters =
  (): Nullable<ReactElement> => {
    const {
      autocompletesRefs: {
        giverCompany: giverCompanyAutocompleteRef,
        collectState: collectStateAutocompleteRef,
        assignedUser: assignedUserAutocompleteRef,
      },
      filters: {
        filtersActives,
        handleClearFilters,
        handleClearFilterByKey,
        handleClearFilterByValues,
      },
    } = useDashboardVariantV2CarrierCollectFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      actions: { handleForceRequests },
    } = useDashboardVariantV2CarrierContentCollectTabContext();

    const onClearFilter = useCallback(() => {
      handleClearFilters(true);
      handleResetSelectedPreset();
    }, [handleClearFilters, handleResetSelectedPreset]);

    const activeFilters = useMemo<OrderActiveFiltersItem[]>(() => {
      const currentFilters: OrderActiveFiltersItem[] = [];

      if (filtersActives.search) {
        currentFilters.push({
          id: 'search',
          label: filtersActives.search,
          tooltip: 'Nro. Orden / Nro. Tracking / Grupo',
          onClear: () =>
            handleClearFilterByKey('search', '', handleForceRequests),
        });
      }

      if (filtersActives.includeWithoutExpirationDate) {
        currentFilters.push({
          id: 'includeWithoutExpirationDate',
          label: 'Sin fecha de vencimiento',
          tooltip: 'Incluir pedidos sin fecha de vencimiento',
          onClear: () =>
            handleClearFilterByKey(
              'includeWithoutExpirationDate',
              null,
              handleForceRequests,
            ),
        });
      }

      const rangeExpirationDates = compact([
        filtersActives.expirationDateTimeFrom,
        filtersActives.expirationDateTimeTo,
      ]);

      if (rangeExpirationDates.length > 0) {
        const rangeExpirationDatesLabel = rangeExpirationDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeExpirationDates',
          label: rangeExpirationDatesLabel,
          tooltip: 'Rango de fechas de vencimiento',
          onClear: () =>
            handleClearFilterByValues(
              { expirationDateTimeFrom: null, expirationDateTimeTo: null },
              handleForceRequests,
            ),
        });
      }

      if (filtersActives.includeWithoutExpirationDate) {
        currentFilters.push({
          id: 'includeWithoutExpirationDate',
          label: 'Sin fecha de vencimiento',
          tooltip: 'Incluir pedidos sin fecha de vencimiento',
          onClear: () =>
            handleClearFilterByKey(
              'includeWithoutExpirationDate',
              null,
              handleForceRequests,
            ),
        });
      }

      const rangeCreationDates = compact([
        filtersActives.dateFrom,
        filtersActives.dateTo,
      ]);

      if (rangeCreationDates.length > 0) {
        const rangeCreationDatesLabel = rangeCreationDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeCreationDates',
          label: rangeCreationDatesLabel,
          tooltip: 'Rango de fechas de creación',
          onClear: () =>
            handleClearFilterByValues(
              { dateFrom: null, dateTo: null },
              handleForceRequests,
            ),
        });
      }

      if (
        filtersActives.collectState !== null &&
        filtersActives.collectState.length > 0
      ) {
        const collectStateLabels = filtersActives.collectState
          .map((service) => service.label.toString())
          .join(' / ');

        currentFilters.push({
          id: 'collectState',
          label: collectStateLabels,
          tooltip: 'Estados',
          onClear: () => {
            collectStateAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('collectState', null, handleForceRequests);
          },
        });
      }

      if (filtersActives.giverCompany) {
        currentFilters.push({
          id: 'giverCompany',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Avatar
                src={getDefaultAvatar(
                  filtersActives.giverCompany.logoFile?.url,
                )}
                alt={filtersActives.giverCompany.name}
                sx={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">
                {filtersActives.giverCompany.name}
              </Typography>
            </Stack>
          ),
          tooltip: 'Dador de carga',
          onClear: () => {
            giverCompanyAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('giverCompany', null, handleForceRequests);
          },
        });
      }

      if (filtersActives.assignedUser) {
        // eslint-disable-next-line max-len
        const assignedUserName = `${filtersActives.assignedUser.lastName}, ${filtersActives.assignedUser.firstName}`;

        currentFilters.push({
          id: 'assignedUser',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Avatar
                src={getDefaultAvatar(
                  filtersActives.assignedUser.avatarUrl || undefined,
                )}
                alt={assignedUserName}
                sx={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{assignedUserName}</Typography>
            </Stack>
          ),
          tooltip: 'Usuario asignado',
          onClear: () => {
            assignedUserAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('assignedUser', null, handleForceRequests);
          },
        });
      }

      return currentFilters;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      filtersActives,
      handleClearFilterByKey,
      handleClearFilterByValues,
      handleForceRequests,
    ]);

    if (activeFilters.length === 0) return null;

    return (
      <DashboardVariantV2CarrierContentActionsContainer>
        <OrderActiveFilters
          filters={activeFilters}
          onClearFilters={onClearFilter}
        />
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentCollectTabCurrenFilters;
